import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Plus, Search, Eye, Trash2, Printer } from 'lucide-react';

const API = 'https://api.ctexpresskh.com/api';
const statusLabel = { pending:'Pending', cn_wh:'CN WH', in_transit:'In Transit', kh_wh:'KH WH', delivery:'Delivery', signed:'Signed' };
const statusColor = { pending:'#f59e0b', cn_wh:'#3b82f6', in_transit:'#8b5cf6', kh_wh:'#06b6d4', delivery:'#f97316', signed:'#10b981' };
const ALL_STATUSES = ['pending','cn_wh','in_transit','kh_wh','delivery','signed'];

export default function Shipments() {
  const navigate = useNavigate();
  const [shipments, setShipments] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalFee, setTotalFee] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState(localStorage.getItem('ct_filter_status') || '');
  const [method, setMethod] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selected, setSelected] = useState([]);
  const [bulkStatus, setBulkStatus] = useState('cn_wh');
  const [updating, setUpdating] = useState(false);
  const token = localStorage.getItem('token');
  const [limit, setLimit] = useState(50);
  const [loadingData, setLoadingData] = useState(false);
  const [companySettings, setCompanySettings] = React.useState({});

  React.useEffect(() => {
    fetch(API + '/settings', { headers: { Authorization: 'Bearer ' + token } })
      .then(r => r.json()).then(d => { if(d.data) setCompanySettings(d.data); });
  }, []);

  const load = useCallback(async (p = 1) => {
    setLoadingData(true);
    const params = new URLSearchParams({ page: p, limit: limit });
    if (search) params.append('search', search);
    if (status) params.append('status', status);
    if (method) params.append('method', method);
    if (dateFrom) params.append('dateFrom', dateFrom);
    if (dateTo) params.append('dateTo', dateTo);
    try {
      const res = await fetch(API + '/shipments?' + params, { headers: { Authorization: 'Bearer ' + token } });
      const data = await res.json();
      setShipments(data.data || []);
      setTotal(data.total || 0);
      setTotalFee(data.total_fee || 0);
      setSelected([]);
    } catch {} finally { setLoadingData(false); }
  }, [search, status, method, dateFrom, dateTo, token, limit]);

  useEffect(() => { setPage(1); load(1); }, [limit]);
  useEffect(() => { load(page); }, [page]);

  const doSearch = () => { setPage(1); load(1); };

  // Auto search when filters change
  useEffect(() => { setPage(1); load(1); }, [status, method, dateFrom, dateTo]);

  // Auto search on typing (debounced)
  useEffect(() => {
    const timer = setTimeout(() => { if(search !== undefined) { setPage(1); load(1); } }, 400);
    return () => clearTimeout(timer);
  }, [search]);
  const clearFilters = () => { setSearch(''); setStatus(''); setMethod(''); setDateFrom(''); setDateTo(''); setPage(1); setTimeout(() => load(1), 100); };
  const toggleSelect = (id) => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleAll = () => setSelected(selected.length === shipments.length ? [] : shipments.map(s => s.id));

  const bulkDelete = async () => {
    if (selected.length === 0) { toast.error('Select shipments first'); return; }
    if (!window.confirm('Delete ' + selected.length + ' selected shipments? This cannot be undone!')) return;
    setUpdating(true);
    let success = 0;
    for (const id of selected) {
      try {
        const res = await fetch(API + '/shipments/' + id, { method: 'DELETE', headers: { Authorization: 'Bearer ' + token } });
        const d = await res.json();
        if (d.success) success++;
      } catch {}
    }
    toast.success('Deleted ' + success + ' shipments!');
    setSelected([]); setUpdating(false); load(1); setPage(1);
  };

  const bulkUpdate = async () => {
    if (selected.length === 0) { toast.error('Select shipments first'); return; }
    setUpdating(true);
    let success = 0;
    for (const id of selected) {
      try {
        const res = await fetch(API + '/shipments/' + id + '/status', {
          method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
          body: JSON.stringify({ status: bulkStatus, note: 'Bulk status update' })
        });
        const d = await res.json();
        if (d.success) success++;
      } catch {}
    }
    toast.success('Updated ' + success + ' shipments to ' + statusLabel[bulkStatus]);
    setSelected([]); setUpdating(false); load(page);
  };

  const deleteShipment = async (id, trk) => {
    if (!window.confirm('Delete shipment ' + trk + '?')) return;
    try {
      const res = await fetch(API + '/shipments/' + id, { method: 'DELETE', headers: { Authorization: 'Bearer ' + token } });
      const d = await res.json();
      if (d.success) { toast.success('Deleted!'); load(page); }
      else toast.error(d.message);
    } catch { toast.error('Error'); }
  };
  const [printModal, setPrintModal] = React.useState(null);
  const [postPrintStatus, setPostPrintStatus] = React.useState('kh_wh');

  const printShipment = (s) => {
    fetch('https://api.ctexpresskh.com/api/settings',{headers:{Authorization:'Bearer '+token}})
      .then(r=>r.json()).then(d=>{
        const cs=d.data||{};
        const lw=parseFloat(cs.label_width||80);
        const lh=parseFloat(cs.label_height||100);
        const date=new Date().toLocaleDateString('en-GB');
        const logoTag=cs.company_logo?"<img src='"+cs.company_logo+"' style='width:28px;height:28px;object-fit:contain;flex-shrink:0'/>":"<div class='logo'>T<br/>F<br/>EX</div>";
        const win=window.open('','_blank');
        const html=[
          "<!DOCTYPE html><html><head><title>"+s.tracking_number+"</title>",
          "<style>@page{size:"+lw+"mm "+lh+"mm;margin:0}*{box-sizing:border-box;margin:0;padding:0}html,body{width:"+lw+"mm;height:"+lh+"mm;font-family:Arial,sans-serif;overflow:hidden}.w{width:"+lw+"mm;height:"+lh+"mm;display:flex;flex-direction:column;border:1.5px solid #000}.hd{display:flex;align-items:center;gap:3px;border-bottom:1.5px solid #000;padding:2px 4px;height:12mm;flex-shrink:0}.logo{border:1.5px solid #000;padding:2px 3px;text-align:center;font-size:7pt;font-weight:900;line-height:1.1;flex-shrink:0}.cn{font-size:9pt;font-weight:900;line-height:1.1}.ci{font-size:5pt;color:#333;line-height:1.3}.dt{text-align:center;font-size:8pt;font-weight:900;border-bottom:1px solid #000;flex-shrink:0;height:6mm;display:flex;align-items:center;justify-content:center;background:#f9f9f9}.it{width:100%;border-collapse:collapse;flex-shrink:0}.it td{padding:1.5px 4px;font-size:6pt;border-bottom:1px solid #eee;line-height:1.3}.lb{color:#555;width:60px;font-size:5.5pt;font-weight:600}.gs{border-top:1px solid #000;flex-shrink:0}.gr{display:flex;padding:1.5px 4px;border-bottom:1px solid #eee;align-items:center}.gz{font-size:5pt;color:#555;width:65px;flex-shrink:0;font-weight:600}.gv{font-size:6.5pt;font-weight:700}.fe{border-top:1.5px solid #000;text-align:center;flex-shrink:0;height:12mm;display:flex;flex-direction:column;align-items:center;justify-content:center;background:#f9f9f9}.fz{font-size:5.5pt;color:#555;font-weight:600}.fv{font-size:13pt;font-weight:900}.ts{border-top:1.5px solid #000;text-align:center;flex-shrink:0;height:26mm;display:flex;flex-direction:column;align-items:center;justify-content:center;padding:2px 4px}.tt{font-size:7.5pt;font-weight:900;margin-bottom:2px}.tn{font-size:6pt;font-weight:700;margin-top:2px;word-break:break-all}svg{width:95%;height:16mm;display:block}.ft{border-top:1px solid #000;flex:1;display:flex;flex-direction:column;justify-content:center;padding:1.5px 4px;background:#f9f9f9}.ft div{font-size:5pt;color:#333;line-height:1.4}@media print{html,body{width:"+lw+"mm;height:"+lh+"mm}}</style>",
          "</head><body><div class='w'>",
          "<div class='hd'>"+logoTag+"<div style='flex:1;margin-left:3px'><div class='cn'>"+(cs.company_name||'CT EXPRESS')+"</div><div class='ci'>Tel: "+(cs.company_tel||'')+"</div><div class='ci'>Addr: "+(cs.company_address||'')+"</div></div></div>",
          "<div class='dt'>Date: "+date+"</div>",
          "<table class='it'><tr><td class='lb'>收件人/Name:</td><td><strong>"+(s.receiver_name||'')+"</strong></td></tr><tr><td class='lb'>电话/Tel:</td><td>"+(s.receiver_phone||'')+"</td></tr><tr><td class='lb'>地址/Addr:</td><td>"+(s.receiver_address||'')+"</td></tr></table>",
          "<div class='gs'><div class='gr'><span class='gz'>立方/CBM:</span><span class='gv'>"+(parseFloat(s.cbm||0)*(s.quantity||1)).toFixed(4)+"m³</span></div><div class='gr'><span class='gz'>数量/Qty:</span><span class='gv'>"+(s.quantity||1)+"</span></div><div class='gr'><span class='gz'>毛重/G.W.(KG):</span><span class='gv'>"+(s.weight_kg||0)+"</span></div></div>",
          "<div class='fe'><div class='fz'>运费/Shipping Cost($):</div><div class='fv'>"+parseFloat(s.shipping_fee||0).toFixed(2)+"</div></div>",
          "<div class='ts'><div class='tt'>跟踪号码 /Tracking Number</div><svg id='bc'></svg><div class='tn'>"+s.tracking_number+"</div></div>",
          "<div class='ft'><div>- "+(cs.label_footer_1||'')+"</div><div>- "+(cs.label_footer_2||'')+"</div></div></div>",
          "<script>function db(el,t){var n=t.replace(/[^0-9]/g,''),x=2,h=13;el.setAttribute('viewBox','0 0 220 16');el.setAttribute('preserveAspectRatio','none');var bg=document.createElementNS('http://www.w3.org/2000/svg','rect');bg.setAttribute('width','220');bg.setAttribute('height','16');bg.setAttribute('fill','#fff');el.appendChild(bg);var e={'0':'3211','1':'2221','2':'2122','3':'1411','4':'1132','5':'1231','6':'1114','7':'1312','8':'1213','9':'3112'};var bk=true;for(var i=0;i<Math.min(n.length,26);i++){var p=(e[n[i]]||'1111').split('');for(var j=0;j<p.length;j++){var w=parseInt(p[j])*1.6;if(bk){var r=document.createElementNS('http://www.w3.org/2000/svg','rect');r.setAttribute('x',x);r.setAttribute('y','1');r.setAttribute('width',w);r.setAttribute('height',h);r.setAttribute('fill','#000');el.appendChild(r);}x+=w;bk=!bk;}}}<\/script>",
          "<script>window.onload=function(){var el=document.getElementById('bc');if(el)db(el,'"+s.tracking_number+"');setTimeout(function(){window.print();},600);window.onafterprint=function(){window.close();};};<\/script></body></html>"
        ].join('');
        win.document.write(html);
        win.document.close();
        // Show post-print status modal
        setTimeout(() => setPrintModal(s), 800);
      });
  };

  const confirmPostPrintStatus = async () => {
    if (!printModal) return;
    try {
      await fetch('https://api.ctexpresskh.com/api/shipments/' + printModal.id + '/status', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
        body: JSON.stringify({ status: postPrintStatus, note: 'Status updated after printing' })
      });
      toast.success('Status updated to ' + statusLabel[postPrintStatus]);
      load(page);
    } catch {}
    setPrintModal(null);
  };

  const totalPages = Math.ceil(total / limit);
  const t = (key, fallback) => window._t && window._t[key] ? window._t[key] : fallback;

  return (
    <div style={{ padding: 28 }}>
      {/* Post-print status modal */}
      {printModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999 }}>
          <div style={{ background:'#fff', borderRadius:16, padding:28, width:360, boxShadow:'0 20px 60px rgba(0,0,0,0.3)' }}>
            <h3 style={{ margin:'0 0 8px', color:'#1e293b', fontSize:17 }}>🖨️ {t('print_status_title','Update Status After Print?')}</h3>
            <p style={{ fontSize:13, color:'#64748b', marginBottom:16 }}>{printModal.tracking_number}</p>
            <p style={{ fontSize:13, color:'#374151', marginBottom:10 }}>{t('print_status_msg','Change shipment status to:')}</p>
            <select value={postPrintStatus} onChange={e => setPostPrintStatus(e.target.value)}
              style={{ width:'100%', padding:'10px 12px', border:'1px solid #d1d5db', borderRadius:8, fontSize:14, marginBottom:16 }}>
              {ALL_STATUSES.map(s => <option key={s} value={s}>{statusLabel[s]}</option>)}
            </select>
            <div style={{ display:'flex', gap:10 }}>
              <button onClick={() => setPrintModal(null)}
                style={{ flex:1, padding:10, border:'1px solid #e2e8f0', borderRadius:8, cursor:'pointer', background:'#fff', fontSize:14 }}>
                {t('btn_cancel','Skip')}
              </button>
              <button onClick={confirmPostPrintStatus}
                style={{ flex:1, padding:10, background:'#f97316', color:'#fff', border:'none', borderRadius:8, cursor:'pointer', fontWeight:700, fontSize:14 }}>
                {t('btn_confirm','Update')}
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <div>
          <h1 style={{ margin:0, fontSize:24, color:'#1e293b' }}>{t('nav_shipments','Shipments')}</h1>
          <div style={{ fontSize:13, color:'#64748b', marginTop:2 }}>
            {t('msg_total','Total')}: <strong>{total}</strong> | Fee: <strong style={{ color:'#f97316' }}>${parseFloat(totalFee).toFixed(2)}</strong>
            {selected.length > 0 && <span style={{ marginLeft:12, color:'#7c3aed', fontWeight:600 }}>| {selected.length} {t('lbl_selected','selected')}</span>}
          </div>
        </div>
        <button onClick={() => navigate('/shipments/create')} style={{ display:'flex', alignItems:'center', gap:8, background:'#f97316', color:'#fff', border:'none', borderRadius:8, padding:'10px 20px', cursor:'pointer', fontWeight:600 }}>
          <Plus size={18} /> {t('btn_new_shipment','New Shipment')}
        </button>
      </div>

      <div style={{ background:'#fff', borderRadius:12, padding:14, marginBottom:14, boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}>
        <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center' }}>
          <div style={{ display:'flex', alignItems:'center', gap:8, flex:1, minWidth:180, border:'1px solid #e2e8f0', borderRadius:8, padding:'7px 12px' }}>
            <Search size={14} color="#94a3b8" />
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder={t('btn_search','Search') + ' tracking, name, phone, ID...'}
              style={{ border:'none', outline:'none', flex:1, fontSize:13 }} />
          </div>
          <select value={status} onChange={e => { setStatus(e.target.value); localStorage.setItem('ct_filter_status', e.target.value); }}
            style={{ padding:'7px 10px', border:'1px solid #e2e8f0', borderRadius:8, fontSize:13 }}>
            <option value="">{t('lbl_all_status','All Status')}</option>
            {ALL_STATUSES.map(s => <option key={s} value={s}>{t('status_'+s, statusLabel[s])}</option>)}
          </select>
          <select value={method} onChange={e => setMethod(e.target.value)}
            style={{ padding:'7px 10px', border:'1px solid #e2e8f0', borderRadius:8, fontSize:13 }}>
            <option value="">{t('lbl_all_methods','All Methods')}</option>
            <option value="sea">Sea</option><option value="land">Land</option><option value="air">Air</option>
          </select>
          <span style={{ fontSize:12, color:'#64748b' }}>{t('lbl_from','From')}</span>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={{ padding:'7px 10px', border:'1px solid #e2e8f0', borderRadius:8, fontSize:13 }} />
          <span style={{ fontSize:12, color:'#64748b' }}>{t('lbl_to','To')}</span>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={{ padding:'7px 10px', border:'1px solid #e2e8f0', borderRadius:8, fontSize:13 }} />
          <button onClick={clearFilters} style={{ padding:'7px 12px', background:'#f1f5f9', border:'none', borderRadius:8, cursor:'pointer', fontSize:13 }}>{t('btn_clear','Clear')}</button>
        </div>
      </div>

      {selected.length > 0 && (
        <div style={{ background:'#ede9fe', border:'1px solid #c4b5fd', borderRadius:10, padding:'10px 16px', marginBottom:12, display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
          <span style={{ fontWeight:700, color:'#7c3aed', fontSize:14 }}>{selected.length} {t('lbl_selected','selected')}</span>
          <select value={bulkStatus} onChange={e => setBulkStatus(e.target.value)} style={{ padding:'6px 10px', border:'1px solid #c4b5fd', borderRadius:8, fontSize:13, background:'#fff' }}>
            {ALL_STATUSES.map(s => <option key={s} value={s}>{t('status_'+s,statusLabel[s])}</option>)}
          </select>
          <button onClick={bulkUpdate} disabled={updating} style={{ padding:'7px 18px', background:'#7c3aed', color:'#fff', border:'none', borderRadius:8, cursor:'pointer', fontWeight:700, fontSize:13, opacity:updating?0.7:1 }}>
            {updating ? '...' : t('btn_update_status','Update Status')}
          </button>
          <button onClick={() => setSelected([])} style={{ padding:'7px 12px', background:'#fff', border:'1px solid #c4b5fd', borderRadius:8, cursor:'pointer', fontSize:13, color:'#7c3aed' }}>{t('btn_clear','Clear')}</button>
          <button onClick={bulkDelete} disabled={updating} style={{ padding:'7px 18px', background:'#dc2626', color:'#fff', border:'none', borderRadius:8, cursor:'pointer', fontWeight:700, fontSize:13, marginLeft:'auto', opacity:updating?0.7:1 }}>
            {t('btn_delete_selected','Delete')} ({selected.length})
          </button>
        </div>
      )}

      <div style={{ background:'#fff', borderRadius:12, boxShadow:'0 1px 4px rgba(0,0,0,0.06)', overflow:'auto' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
          <thead style={{ background:'#f8fafc' }}>
            <tr>
              <th style={{ padding:'10px 14px', borderBottom:'1px solid #e2e8f0', width:36 }}>
                <input type="checkbox" checked={selected.length===shipments.length && shipments.length>0} onChange={toggleAll} style={{ cursor:'pointer', width:15, height:15 }} />
              </th>
              {[['col_tracking','Tracking'],['col_user_id','User ID'],['col_receiver','Receiver'],['col_description','Description'],['col_method','Method'],['col_type','Type'],['col_qty','ចំនួន/Qty'],['col_weight','Weight'],['col_cbm','CBM(m³)'],['col_fee','Fee($)'],['col_status','Status'],['col_date','Date'],['col_actions','Actions']].map(([k,v]) => (
                <th key={k} style={{ padding:'10px 12px', textAlign:'left', color:'#64748b', fontWeight:600, borderBottom:'1px solid #e2e8f0', whiteSpace:'nowrap' }}>{t(k,v)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shipments.length === 0 ? (
              <tr><td colSpan={12} style={{ padding:40, textAlign:'center', color:'#94a3b8' }}>{t('msg_no_shipments','No shipments found')}</td></tr>
            ) : shipments.map(s => (
              <tr key={s.id} style={{ borderBottom:'1px solid #f8fafc', background:selected.includes(s.id)?'#faf5ff':'#fff' }}
                onMouseEnter={e => { if(!selected.includes(s.id)) e.currentTarget.style.background='#fafafa'; }}
                onMouseLeave={e => { e.currentTarget.style.background=selected.includes(s.id)?'#faf5ff':'#fff'; }}>
                <td style={{ padding:'9px 14px' }}>
                  <input type="checkbox" checked={selected.includes(s.id)} onChange={() => toggleSelect(s.id)} style={{ cursor:'pointer', width:15, height:15 }} />
                </td>
                <td style={{ padding:'9px 12px', fontWeight:700, color:'#f97316', whiteSpace:'nowrap', fontSize:12 }}>{s.tracking_number}</td>
                <td style={{ padding:'9px 12px', fontSize:12, whiteSpace:'nowrap' }}>
                  <div style={{ fontWeight:700, color:'#f97316', fontSize:11 }}>{s.receiver_customer_code||'-'}</div>
                  <div style={{ color:'#94a3b8', fontSize:11 }}>{s.receiver_phone}</div>
                </td>
                <td style={{ padding:'9px 12px' }}><div style={{ fontWeight:500 }}>{s.receiver_name||'-'}</div></td>
                <td style={{ padding:'9px 12px', color:'#64748b', maxWidth:140, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{s.goods_description||'-'}</td>
                <td style={{ padding:'9px 12px', fontWeight:600, textTransform:'uppercase', fontSize:12 }}>{s.shipping_method}</td>
                <td style={{ padding:'9px 12px' }}>
                  <span style={{ background:s.shipping_type==='cbm'?'#dbeafe':'#fef3c7', color:s.shipping_type==='cbm'?'#1d4ed8':'#92400e', padding:'2px 7px', borderRadius:10, fontSize:11, fontWeight:700 }}>
                    {(s.shipping_type||'').toUpperCase()}
                  </span>
                </td>
                <td style={{ padding:'9px 12px', textAlign:'center', fontWeight:700, color:'#1e293b' }}>{s.quantity||1}</td>
                <td style={{ padding:'9px 12px', color:'#64748b', whiteSpace:'nowrap' }}>{s.weight_kg?s.weight_kg+'kg':'-'}</td>
                <td style={{ padding:'9px 12px', color:'#64748b', whiteSpace:'nowrap' }}>{s.cbm?( parseFloat(s.cbm)*(s.quantity||1)).toFixed(4):'-'}</td>
                <td style={{ padding:'9px 12px', fontWeight:700, color:'#1e293b' }}>${parseFloat(s.shipping_fee||0).toFixed(2)}</td>
                <td style={{ padding:'9px 12px' }}>
                  <span style={{ background:(statusColor[s.status]||'#gray')+'20', color:statusColor[s.status], padding:'3px 9px', borderRadius:20, fontSize:11, fontWeight:700, whiteSpace:'nowrap' }}>
                    {t('status_'+s.status, statusLabel[s.status]||s.status)}
                  </span>
                </td>
                <td style={{ padding:'9px 12px', color:'#94a3b8', whiteSpace:'nowrap', fontSize:12 }}>{new Date(s.updated_at||s.created_at).toLocaleDateString('en-GB', {timeZone:'Asia/Phnom_Penh'})}</td>
                <td style={{ padding:'9px 12px' }}>
                  <div style={{ display:'flex', gap:4 }}>
                    <button onClick={() => navigate('/shipments/'+s.id)} title="View" style={{ background:'#dbeafe', border:'none', borderRadius:6, padding:'5px 7px', cursor:'pointer', color:'#1d4ed8' }}><Eye size={12} /></button>
                    <button onClick={() => printShipment(s)} title={t('btn_print','Print')} style={{ background:'#d1fae5', border:'none', borderRadius:6, padding:'5px 7px', cursor:'pointer', color:'#059669' }}><Printer size={12} /></button>
                    <button onClick={() => deleteShipment(s.id, s.tracking_number)} title="Delete" style={{ background:'#fee2e2', border:'none', borderRadius:6, padding:'5px 7px', cursor:'pointer', color:'#dc2626' }}><Trash2 size={12} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
          {shipments.length > 0 && (
            <tfoot style={{ background:'#fafafa', borderTop:'2px solid #e2e8f0' }}>
              <tr>
                <td colSpan={7} style={{ padding:'10px 14px', fontWeight:700, color:'#1e293b' }}>{t('msg_total','Total')} ({total})</td>
                <td style={{ padding:'10px 12px', fontWeight:700, color:'#1e293b', textAlign:'center' }}>
                  {shipments.reduce((s,r)=>s+(parseInt(r.quantity)||1),0)}
                </td>
                <td style={{ padding:'10px 12px', fontWeight:700, color:'#1e293b' }}>
                  {shipments.reduce((s,r)=>s+parseFloat(r.weight_kg||0),0).toFixed(3)}kg
                </td>
                <td style={{ padding:'10px 12px', fontWeight:700, color:'#1e293b' }}>
                  {shipments.reduce((s,r)=>s+(parseFloat(r.cbm||0)*(parseInt(r.quantity)||1)),0).toFixed(4)}
                </td>
                <td style={{ padding:'10px 12px', fontWeight:800, color:'#f97316', fontSize:15 }}>${parseFloat(totalFee).toFixed(2)}</td>
                <td colSpan={3}></td>
              </tr>
            </tfoot>
          )}
        </table>
        <div style={{ padding:'10px 16px', display:'flex', justifyContent:'space-between', alignItems:'center', borderTop:'1px solid #f1f5f9' }}>
          <span style={{ fontSize:13, color:'#64748b' }}>{t('msg_showing','Showing')} {((page-1)*limit)+1}-{Math.min(page*limit,total)} of {total}</span>
            <select value={limit} onChange={e => { setLimit(parseInt(e.target.value)); setPage(1); }} style={{ padding:'5px 10px', border:'1px solid #e2e8f0', borderRadius:6, fontSize:13, cursor:'pointer', marginLeft:8 }}>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={500}>500</option>
              <option value={1000}>1000</option>
              <option value={9999}>All</option>
            </select>
          <div style={{ display:'flex', gap:6, alignItems:'center' }}>
            <button onClick={() => setPage(1)} disabled={page===1} style={{ padding:'5px 9px', border:'1px solid #e2e8f0', borderRadius:6, cursor:'pointer', background:'#fff', fontSize:12 }}>«</button>
            <button onClick={() => setPage(p=>Math.max(1,p-1))} disabled={page===1} style={{ padding:'5px 10px', border:'1px solid #e2e8f0', borderRadius:6, cursor:'pointer', background:'#fff', fontSize:13 }}>Prev</button>
            <span style={{ padding:'5px 12px', fontSize:13, fontWeight:600 }}>Page {page}/{totalPages}</span>
            <button onClick={() => setPage(p=>Math.min(totalPages,p+1))} disabled={page===totalPages} style={{ padding:'5px 10px', border:'1px solid #e2e8f0', borderRadius:6, cursor:'pointer', background:'#fff', fontSize:13 }}>Next</button>
            <button onClick={() => setPage(totalPages)} disabled={page===totalPages} style={{ padding:'5px 9px', border:'1px solid #e2e8f0', borderRadius:6, cursor:'pointer', background:'#fff', fontSize:12 }}>»</button>
          </div>
        </div>
      </div>
    </div>
  );
}
